import { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AccountCircle } from "@mui/icons-material";
import setSelectedOrganisation from "../../Functions/setSelectedOrganisation";
import refreshData from "../../Functions/refreshData";

/**
 * Custom hook for NavBar state management
 */
export default function useNavBar(handleCollapsed, handleThemeChange) {
  const { logout, user } = useAuth0();
  const navigate = useNavigate();

  // Redux selectors
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const organisations = useSelector((state) => state.organisations);
  const themeMode = useSelector((state) => state.themeMode);
  const isMobile = useSelector((state) => state.isMobile);

  // Local state
  const [rotateChevron, setRotateChevron] = useState(
    localStorage.getItem("sidebarCollapsed") === "true" ? true : false
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSidebar, setAnchorElSidebar] = useState(null);
  const [userImage, setUserImage] = useState(
    <AccountCircle sx={{ color: "white" }} />
  );

  // Compute rotation style
  const rotate = rotateChevron ? "rotate(-180deg)" : "rotate(0)";

  // Handle sidebar collapse
  const handleCollapsedMenu = useCallback(() => {
    if (localStorage.getItem("sidebarCollapsed") === "true") {
      localStorage.setItem("sidebarCollapsed", "false");
    } else {
      localStorage.setItem("sidebarCollapsed", "true");
    }
    setRotateChevron(!rotateChevron);
    handleCollapsed();
  }, [rotateChevron, handleCollapsed]);

  // Handle organisation change
  const handleOrganisationChange = useCallback(
    (newValue) => {
      if (newValue) {
        setSelectedOrganisation(newValue);
        localStorage.setItem("selectedOrganisationID", newValue.id);
        refreshData(user.email, user.sub);

        // If pageurl contains device, redirect to root
        if (window.location.href.includes("device")) {
          navigate("/");
        }
      }
    },
    [user, navigate]
  );

  // Handle menu open
  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // Handle menu close
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Handle sidebar menu close
  const handleCloseSidebar = useCallback(() => {
    setAnchorElSidebar(null);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("selectedOrganisationID");
    logout({
      returnTo: window.location.origin,
    });
  }, [logout]);

  // Set user image when user changes
  useEffect(() => {
    if (user && user.picture) {
      setUserImage(
        <img src={user.picture} className="user-image" alt={user.name} />
      );
    }
  }, [user]);

  return {
    // State
    user,
    themeMode,
    isMobile,
    selectedOrganisation,
    organisations,
    rotateChevron,
    rotate,
    anchorEl,
    anchorElSidebar,
    userImage,

    // Actions
    handleThemeChange,
    handleMenu,
    handleClose,
    handleCloseSidebar,
    handleCollapsedMenu,
    handleOrganisationChange,
    handleLogout,
    setAnchorElSidebar,

    // Navigation
    navigate,
  };
}
